import { Row } from '@tanstack/react-table';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { FC } from 'react';
// Local inline module declaration to satisfy TypeScript when ambient file not picked up
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import LoadingSpinner from '@/components/Button/LoadingSpinner/LoadingSpinner';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import ProgressIndicator from '@/components/ProgressIndicator/ProgressIndicator';
import { ProgressOptions } from '@/components/StatusTracker/StatusTracker.types';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import { useNationalProgramDetails } from '@/services/bundles/bundles.service';
import { OptInDetails } from '@/types/nationalProgram.types';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import styles from './NationalProgramDetails.module.css';

const NationalProgramDetails: FC = () => {
  const { t } = useTranslation();
  const { bundleId, nationalProgramId } = useParams();
  const navigateWithToast = useNavigateWithToast();

  const hasProgramId = typeof nationalProgramId === 'string' && nationalProgramId.length > 0;
  const {
    data: nationalProgramData,
    isError,
    isLoading,
  } = useNationalProgramDetails(hasProgramId, hasProgramId ? nationalProgramId : '');

  if (isError) {
    navigateWithToast('/', {
      label: t('error.fetchingNationalProgramDetails'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingNationalProgramDetails',
    });
  }

  const pricePointMetrics = nationalProgramData?.data?.programMetrics?.pricePointsMetrics || [];
  const optInStartDate = nationalProgramData?.data?.program?.optInStartDate || '';
  const freezeStartDate = nationalProgramData?.data?.program?.freezeStartDate || '';
  const freezeEndDate = nationalProgramData?.data?.program?.freezeEndDate || '';
  const redeemableOnline = nationalProgramData?.data?.program?.redeemableOnline || '';
  const visibleOnline = nationalProgramData?.data?.program?.visibleOnline || '';
  const optOutHidden = nationalProgramData?.data?.program?.optOutHidden || '';

  const roundToNearestTwo = (value: number) => {
    if (value > 0 && value < 0.01) return 0.01;
    if (Number.isInteger(value)) return +value.toFixed(0);
    return +value.toFixed(2);
  };

  const headers = [
    { label: t('nationalProgramDetails.table.storeId'), key: 'storeNo' },
    { label: t('nationalProgramDetails.table.storeGroupName'), key: 'storeGroupName' },
    { label: t('nationalProgramDetails.table.franchisee'), key: 'fzCode' },
    { label: t('nationalProgramDetails.table.orgId'), key: 'orgId' },
    { label: t('nationalProgramDetails.table.dma'), key: 'dma' },
    { label: t('nationalProgramDetails.table.pricePoint'), key: 'chosenPrice' },
    { label: t('nationalProgramDetails.table.userId'), key: 'updatedBy' },
    { label: t('nationalProgramDetails.table.optInDate'), key: 'optInDate' },
    { label: t('nationalProgramDetails.table.name'), key: 'name' },
    { label: t('nationalProgramDetails.table.optInStartDate'), key: 'optInStartDate' },
    { label: t('nationalProgramDetails.table.freezeStartDate'), key: 'freezeStartDate' },
    { label: t('nationalProgramDetails.table.freezeEndDate'), key: 'freezeEndDate' },
    { label: t('nationalProgramDetails.table.redeemableOnline'), key: 'redeemableOnline' },
    { label: t('nationalProgramDetails.table.visibleOnline'), key: 'visibleOnline' },
    { label: t('nationalProgramDetails.table.optOutSetting'), key: 'optOutSetting' },
    { label: t('nationalProgramDetails.table.nationalPrice'), key: 'nationalPrice' },
    { label: t('nationalProgramDetails.table.optInPercentNationalPrice'), key: 'optInPercentNationalPrice' },
    { label: t('nationalProgramDetails.table.pricePoint2'), key: 'pricePoint2' },
    { label: t('nationalProgramDetails.table.optInPercentPricePoint2'), key: 'optInPercentPricePoint2' },
    { label: t('nationalProgramDetails.table.pricePoint3'), key: 'pricePoint3' },
    { label: t('nationalProgramDetails.table.optInPercentPricePoint3'), key: 'optInPercentPricePoint3' },
    { label: t('nationalProgramDetails.table.pricePoint4'), key: 'pricePoint4' },
    { label: t('nationalProgramDetails.table.optInPercentPricePoint4'), key: 'optInPercentPricePoint4' },
    { label: t('nationalProgramDetails.table.optInPercentOptOut'), key: 'optOutPercent' },
  ];

  // Build CSV export rows
  const csvData: Record<string, unknown>[] = [];

  let pricePoint2;
  let optInPercentPricePoint2;
  let pricePoint3;
  let optInPercentPricePoint3;
  let pricePoint4;
  let optInPercentPricePoint4;

  nationalProgramData?.data?.programMetrics?.pricePointsMetrics.forEach((price, index) => {
    if (index === 0) {
      pricePoint2 = price?.pricePoint ? `$${price?.pricePoint.toFixed(2)}` : '$0.00';
      optInPercentPricePoint2 = price.optInPercent;
    } else if (index === 1) {
      pricePoint3 = price?.pricePoint ? `$${price?.pricePoint.toFixed(2)}` : '$0.00';
      optInPercentPricePoint3 = price.optInPercent;
    } else if (index === 2) {
      pricePoint4 = price?.pricePoint ? `$${price?.pricePoint.toFixed(2)}` : '$0.00';
      optInPercentPricePoint4 = price.optInPercent;
    }
  });

  // First line for the Program details
  csvData.push({
    storeNo: '',
    storeGroupName: '',
    orgId: '',
    fzCode: '',
    dma: '',
    chosenPrice: '',
    updatedBy: '',
    optInDate: '',
    name: nationalProgramData?.data?.program?.name,
    optInStartDate: DateTime.fromISO(optInStartDate, { setZone: true }).toFormat('D'),
    freezeStartDate: DateTime.fromISO(freezeStartDate, { setZone: true }).toFormat('D'),
    freezeEndDate: DateTime.fromISO(freezeEndDate, { setZone: true }).toFormat('D'),
    redeemableOnline: nationalProgramData?.data?.program?.redeemableOnline,
    visibleOnline: nationalProgramData?.data?.program?.visibleOnline,
    optOutSetting: nationalProgramData?.data?.program?.optOutHidden ? 'Hide' : 'Show',
    nationalPrice: nationalProgramData?.data?.programMetrics?.defaultPriceMetrics.pricePoint
      ? `$${nationalProgramData?.data?.programMetrics?.defaultPriceMetrics.pricePoint.toFixed(2)}`
      : '$0.00',
    optInPercentNationalPrice: `${nationalProgramData?.data?.programMetrics?.defaultPriceMetrics.optInPercent}`,
    pricePoint2,
    optInPercentPricePoint2,
    pricePoint3,
    optInPercentPricePoint3,
    pricePoint4,
    optInPercentPricePoint4,
    optOutPercent:
      nationalProgramData?.data?.programMetrics?.optOutPercent !== undefined
        ? `${roundToNearestTwo(nationalProgramData?.data?.programMetrics?.optOutPercent)}%`
        : '0%',
  });

  // Empty Line break
  csvData.push({
    name: '',
    optInStartDate: '',
    freezeStartDate: '',
    optOutPercent: '',
    freezeEndDate: '',
    redeemableOnline: '',
    visibleOnline: '',
    optOutSetting: '',
    pricePoint: '',
    optIn: '',
  });

  // OptIn Details
  nationalProgramData?.data?.optInDetails?.forEach((details) => {
    csvData.push({
      storeNo: details.storeNo,
      orgId: details.orgId,
      dma: details.dma,
      chosenPrice: details?.chosenPrice ? `$${details?.chosenPrice.toFixed(2)}` : '$0.00',
      fzCode: details.fzCode,
      updatedBy: details.updatedBy,
      storeGroupName: details.storeGroupName,
      optInDate: DateTime.fromISO(details.optInDate, { setZone: true }).toFormat('D'),
    });
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <Header
        title={t('nationalProgramDetails.title')}
        breadcrumb={{ label: t('breadcrumb.backToBundle'), to: `/bundles/${bundleId}` }}
      />
      <div className={styles.page}>
        <div className={classNames(styles.topContainer, styles.whiteBackground)}>
          <div className={styles.titleRow}>
            <h2>{t('nationalProgramDetails.header')}</h2>
            {nationalProgramData && (
              <CSVLink data={csvData} headers={headers} filename="NationalProgramExport.csv">
                <Button variant={ButtonVariants.Secondary}>{t('common.printExportButton')}</Button>
              </CSVLink>
            )}
          </div>
          <Card>
            <div className={styles.programDetails}>
              <div className={styles.column}>
                <div className={styles.row}>
                  <span className={styles.label}>{t('nationalProgramDetails.optInDate')}:</span>
                  <span>{DateTime.fromISO(optInStartDate, { setZone: true }).toFormat('D')}</span>
                </div>
                <div className={styles.row}>
                  <div className={styles.labelWithTooltip}>
                    <span className={styles.label}>{t('nationalProgramDetails.optInProgress')}:</span>
                    <Tooltip
                      position={PositionTypes.Top}
                      content={t('nationalProgramDetails.optInProgressTooltip')}
                      size={18}
                    />
                  </div>
                  <span>
                    {nationalProgramData?.data?.programMetrics?.optOutPercent !== undefined
                      ? `${100 - roundToNearestTwo(nationalProgramData.data.programMetrics.optOutPercent)}%`
                      : '-'}
                  </span>
                </div>
                <div>
                  <div className={styles.progressIndicator}>
                    <ProgressIndicator
                      progress={
                        nationalProgramData?.data?.programMetrics?.optOutPercent !== undefined
                          ? ((100 -
                              roundToNearestTwo(
                                nationalProgramData.data.programMetrics.optOutPercent,
                              )) as ProgressOptions)
                          : 0
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.row}>
                  <span className={styles.label}>{t('common.startDate')}:</span>
                  <span>{DateTime.fromISO(freezeStartDate, { setZone: true }).toFormat('D')}</span>
                </div>
                <div className={styles.row}>
                  <span className={styles.label}>{t('common.endDate')}:</span>
                  <span>{DateTime.fromISO(freezeEndDate, { setZone: true }).toFormat('D')}</span>
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.row}>
                  <span className={styles.label}>{t('nationalProgramDetails.redeemableOnline')}:</span>
                  <span>{redeemableOnline ? t('common.yes') : t('common.no')}</span>
                </div>
                <div className={styles.row}>
                  <span className={styles.label}>{t('nationalProgramDetails.visibleOnline')}:</span>
                  <span>{visibleOnline ? t('common.yes') : t('common.no')}</span>
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.row}>
                  <span className={styles.label}>{t('nationalProgramDetails.OptOutSetting')}:</span>
                  <span>{optOutHidden ? t('common.hide') : t('common.show')}</span>
                </div>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.metricDetails}>
              <h3>{t('nationalProgramDetails.optInOverall')}</h3>
              <div className={styles.blocksContainer}>
                <div className={styles.blocksCard}>
                  {/* Default Price */}
                  <div className={classNames(styles.blockContent, { [styles.rightBorder]: true })}>
                    <div className={styles.percentage}>
                      {nationalProgramData?.data?.programMetrics?.defaultPriceMetrics?.optInPercent !== undefined
                        ? `${roundToNearestTwo(
                            nationalProgramData?.data?.programMetrics?.defaultPriceMetrics?.optInPercent,
                          )}%`
                        : '0%'}
                    </div>
                    <div className={styles.text}>{t('nationalProgramDetails.nationalPrice')}</div>
                    <div className={styles.subText}>
                      {nationalProgramData?.data?.programMetrics?.defaultPriceMetrics?.pricePoint !== undefined
                        ? `$${nationalProgramData?.data?.programMetrics?.defaultPriceMetrics?.pricePoint.toFixed(2)}`
                        : '$0.00'}
                    </div>
                  </div>
                  {/* Out of Scope: HAG Default Price */}
                  {/* <div className={classNames(styles.blockContent, { [styles.rightBorder]: true })}>
                    <div className={styles.percentage}>
                      {nationalProgramData?.data?.programMetrics?.defaultPriceHagMetrics?.optInPercent !== undefined
                        ? `${roundToNearestTwo(
                            nationalProgramData.data.programMetrics.defaultPriceHagMetrics.optInPercent,
                          )}%`
                        : '0%'}
                    </div>
                    <div className={styles.text}>Hawaii / Alaska / Guam</div>
                    <div className={styles.subText}>
                      {nationalProgramData?.data?.programMetrics?.defaultPriceHagMetrics?.pricePoint
                        ? `$${nationalProgramData?.data?.programMetrics?.defaultPriceHagMetrics?.pricePoint.toFixed(2)}`
                        : '$0.00'}
                    </div>
                  </div> */}
                  {/* Price Points */}
                  {pricePointMetrics.map((price, index) => (
                    <div
                      key={price.pricePoint}
                      className={classNames(styles.blockContent, {
                        [styles.rightBorder]: pricePointMetrics.length !== 1 && index !== pricePointMetrics.length - 1,
                      })}
                    >
                      <div className={styles.percentage}>
                        {price?.optInPercent !== undefined ? `${roundToNearestTwo(price.optInPercent)}%` : '0%'}
                      </div>
                      <div className={styles.text}>{t('nationalProgramDetails.pricePoint', { number: index + 2 })}</div>
                      <div className={styles.subText}>
                        {price?.pricePoint ? `$${price?.pricePoint.toFixed(2)}` : '$0.00'}
                      </div>
                    </div>
                  ))}
                  {/* Opt-Out Metrics */}
                  <div className={classNames(styles.blockContent, { [styles.leftBorder]: true })}>
                    <div className={styles.percentage}>
                      {nationalProgramData?.data?.programMetrics?.optOutPercent !== undefined
                        ? `${roundToNearestTwo(nationalProgramData?.data?.programMetrics?.optOutPercent)}%`
                        : '0%'}
                    </div>
                    <div className={styles.text}>{t('nationalProgramDetails.optOut')}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className={classNames(styles.bottomContainer, styles.greyBackground)}>
          <TableCard
            title={t('nationalProgramDetails.optInPeriodDetails')}
            data={nationalProgramData?.data?.optInDetails || []}
            columns={[
              {
                accessorKey: 'storeNo',
                header: t('nationalProgramDetails.table.storeId'),
                enableSorting: true,
              },
              {
                accessorKey: 'fzCode',
                header: t('nationalProgramDetails.table.franchisee'),
                enableSorting: true,
              },
              {
                accessorKey: 'dma',
                header: t('nationalProgramDetails.table.dma'),
                enableSorting: true,
              },
              {
                accessorKey: 'chosenPrice',
                header: t('nationalProgramDetails.table.pricePoint'),
                enableSorting: true,
                cell: (props) =>
                  props.row.original.chosenPrice
                    ? `$${props.row.original.chosenPrice.toFixed(2)}`
                    : t('nationalProgramDetails.optOut'),
                filterFn: (row: Row<OptInDetails>, columnId: string, filterValue) => {
                  const rowPrice = row.original.chosenPrice
                    ? row.original.chosenPrice
                    : t('nationalProgramDetails.optOut').toLowerCase();
                  if (rowPrice.toString().includes(filterValue.toLowerCase())) return true;
                  return false;
                },
              },
            ]}
            returnedRecordsText={(count) => t('nationalProgram.table.returnedRecord', { count })}
            enableFilters
          />
        </div>
      </div>
    </>
  );
};

export default NationalProgramDetails;
